import type { ReactNode } from 'react';
import '../WoodButton.css';
import './IconButton.css';

type IconButtonVariant = 'primary' | 'secondary' | 'ghost';
type IconButtonShape = 'circle' | 'square';
type IconButtonSize = 'sm' | 'md' | 'lg';

interface IconButtonProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: IconButtonVariant;
  shape?: IconButtonShape;
  size?: IconButtonSize;
  disabled?: boolean;
  className?: string;
  'aria-label': string;
  'aria-pressed'?: boolean;
  title?: string;
}

const VARIANT_CLASS: Record<IconButtonVariant, string> = {
  primary: '',
  secondary: 'btn-secondary',
  ghost: 'btn-ghost',
};

/**
 * Icon-only pushable button. Shares its visual language with {@link Button} —
 * same `primary` / `secondary` / `ghost` variants — but sized as a compact
 * square or circle so it fits a single glyph or SVG icon.
 */
export function IconButton({
  children,
  onClick,
  variant = 'secondary',
  shape = 'circle',
  size = 'md',
  disabled,
  className,
  'aria-label': ariaLabel,
  'aria-pressed': ariaPressed,
  title,
}: IconButtonProps) {
  return (
    <button
      type="button"
      className={`pushable push-icon push-icon-${size} push-icon--${shape} ${VARIANT_CLASS[variant]} ${className ?? ''}`.trim()}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      title={title}
    >
      <span className="shadow" />
      <span className="edge" />
      <span className="front">{children}</span>
    </button>
  );
}
